import { useState } from 'react';
import { Copy, Check, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCreateOrRotateApiKey } from '@/hooks/admin/organization/use-create-or-rotate-api-key';
import type { ApiKeyInfo } from '@/hooks/admin/organization/use-api-key-info';

interface ApiKeyModalProps {
  open: boolean;
  onClose: () => void;
  apiKeyInfo: ApiKeyInfo;
}

/**
 * Modal for generating or rotating an API key.
 *
 * Views are derived from props and minimal state:
 * 1. Rotation Confirmation: apiKeyInfo.exists && !generatedKey
 * 2. Initial Generate: !apiKeyInfo.exists && !generatedKey
 * 3. Display Key: generatedKey is set
 */
export function ApiKeyModal({ open, onClose, apiKeyInfo }: ApiKeyModalProps) {
  // When set, the modal displays the generated key for one-time display
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  // Briefly show "Copied" when the user copies the key to clipboard
  const [copyButtonText, setCopyButtonText] = useState<'Copy' | 'Copied'>(
    'Copy'
  );
  // Used to prevent closing the modal until the user has confirmed they have saved the key
  const [savedConfirmed, setSavedConfirmed] = useState(false);

  const { createOrRotateApiKey, isLoading, error } = useCreateOrRotateApiKey({
    onSuccess: (data) => setGeneratedKey(data.apiKey),
    onError: (err) => console.error('Failed to generate API key:', err),
  });

  // Derived from props - no state needed
  const isRotation = apiKeyInfo.exists;
  const title = isRotation ? 'Rotate API Key' : 'Generate API Key';

  const handleGenerate = async () => {
    await createOrRotateApiKey();
  };

  const handleCopy = async () => {
    if (generatedKey) {
      await navigator.clipboard.writeText(generatedKey);
      setCopyButtonText('Copied');
      setTimeout(() => setCopyButtonText('Copy'), 2000);
    }
  };

  const handleCancel = () => {
    setGeneratedKey(null);
    setSavedConfirmed(false);
    onClose();
  };

  // View 3: Display the generated key (takes precedence when key exists)
  if (generatedKey) {
    return (
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-lg [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>
              {isRotation ? 'New API Key Generated' : 'API Key Generated'}
            </DialogTitle>
            <DialogDescription>
              <div className="space-y-4 mt-2">
                {/* Warning banner */}
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">
                    <strong>Make sure to copy your API key now.</strong> You
                    won't be able to see it again! If you lose this key, you'll
                    need to generate a new one.
                  </span>
                </div>

                {/* Key display */}
                <div className="relative">
                  <div className="p-4 rounded-lg bg-zinc-900 border border-zinc-700 font-mono text-sm text-zinc-100 break-all select-all">
                    {generatedKey}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopy}
                    className="absolute top-2 right-2 h-8 px-2 bg-zinc-800 border-zinc-600 hover:bg-zinc-700 text-zinc-100"
                  >
                    {copyButtonText === 'Copied' ? (
                      <Check className="h-4 w-4 mr-1 text-green-400" />
                    ) : (
                      <Copy className="h-4 w-4 mr-1" />
                    )}
                    {copyButtonText}
                  </Button>
                </div>

                {/* Confirmation checkbox */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="saved-confirmation"
                    checked={savedConfirmed}
                    onCheckedChange={(checked) =>
                      setSavedConfirmed(checked === true)
                    }
                  />
                  <label
                    htmlFor="saved-confirmation"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    I have saved this key
                  </label>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={onClose} disabled={!savedConfirmed}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // View 1: Rotation confirmation (when key exists, warn before rotating)
  if (isRotation) {
    return (
      <Dialog open={open} onOpenChange={handleCancel}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription className="space-y-3">
              <p>
                Are you sure you want to rotate your API key? The current key
                will be <strong>immediately invalidated</strong> and any
                applications using it will stop working.
              </p>
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">
                  This action cannot be undone. Make sure you're ready to update
                  your API key in all applications that use it.
                </span>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={isLoading}
              variant="destructive"
            >
              {isLoading ? 'Generating...' : 'Rotate Key'}
            </Button>
          </DialogFooter>
          {error && (
            <p className="text-sm text-red-600 mt-2">
              {error.message || 'Failed to generate API key'}
            </p>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // View 2: Initial generate (no existing key)
  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Click submit to generate and reveal your API key. This key will
            allow programmatic access to your organization.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleGenerate} disabled={isLoading}>
            {isLoading ? 'Generating...' : 'Generate Key'}
          </Button>
        </DialogFooter>
        {error && (
          <p className="text-sm text-red-600 mt-2">
            {error.message || 'Failed to generate API key'}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
